import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Modal, Button } from "react-bootstrap";
import { matchOtherCharges } from "../utils/otherChargeOptions";

function parseChargeSuggestion(suggestion) {
  const text = suggestion ? String(suggestion) : "";
  const codeMatch = text.match(/\(([^)]+)\)\s*$/);
  const code = codeMatch ? codeMatch[1].trim() : "";
  const name = codeMatch ? text.slice(0, codeMatch.index).trim() : text.trim();
  return {
    name,
    codes: code ? [code] : [],
    priceTo: "",
    price: null,
  };
}

export const InlineAutoCompleteInput = ({
  value,
  onChange,
  onFocus,
  onBlur,
  suggestions = [],
  chargeOptions = [],
  onComplete,
  id,
  className,
  style,
  ...props
}) => {
  const inputRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuPos, setMenuPos] = useState(null);

  const typed = String(value ?? "").trim();
  const matches = chargeOptions.length
    ? matchOtherCharges(chargeOptions, typed)
    : matchOtherCharges(
        (suggestions || []).map(parseChargeSuggestion).filter((row) => row.name),
        typed,
      );
  const showMenu = isActive && typed.length > 0 && matches.length > 0;

  useEffect(() => {
    setActiveIndex(0);
  }, [typed]);

  useEffect(() => {
    if (!showMenu) return undefined;
    const place = () => {
      const el = inputRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 2,
        left: rect.left,
        width: Math.max(rect.width, 220),
      });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [showMenu, typed, matches.length]);

  const selectMatch = (match) => {
    if (!match?.name) return;
    onComplete(match.name);
    setIsActive(false);
  };

  return (
    <div className="position-relative" style={{ width: "100%" }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={onChange}
        onFocus={(e) => {
          setIsActive(true);
          onFocus && onFocus(e);
        }}
        onBlur={(e) => {
          setTimeout(() => setIsActive(false), 150);
          onBlur && onBlur(e);
        }}
        onKeyDown={(e) => {
          if (!showMenu) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((index) => (index + 1) % matches.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex(
              (index) => (index - 1 + matches.length) % matches.length,
            );
          } else if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            selectMatch(matches[activeIndex] || matches[0]);
          }
        }}
        className={className}
        style={style}
        {...props}
      />
      {showMenu &&
        menuPos &&
        ReactDOM.createPortal(
          <div
            className="dropdown-menu show"
            style={{
              display: "block",
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              zIndex: 20000,
              maxHeight: 220,
              overflowY: "auto",
              padding: "0.25rem 0",
            }}
          >
            {matches.slice(0, 8).map((match, index) => (
                <button
                  key={`${match.name}-${index}`}
                  type="button"
                  className={`dropdown-item${index === activeIndex ? " active" : ""}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectMatch(match);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  {match.name}
                </button>
              ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

const OtherAmountModal = ({
  otherAmountModal,
  setOtherAmountModal,
  cardClass,
  otherAmountColumnWidths,
  handleOtherAmountColumnWidthChange,
  suggestions,
  setSuggestions,
  getSuggestions,
  formatAmount,
  calculateAmount,
  rateOnOptions,
  highlightedIndex,
  setHighlightedIndex,
  handleRateOnDropdown,
  handleRateOnKeyDown,
  calculateLessWeight,
  formatLessWeight,
  calculateTotal,
  buttonClass,
  handleSaveOtherAmountEntries,
  setTableData,
  applyChargeType,
  chargeOptions = [],
}) => {
  if (!otherAmountModal.isOpen) return null;

  return ReactDOM.createPortal(
    <Modal
      show={otherAmountModal.isOpen}
      onHide={() => setOtherAmountModal((prev) => ({ ...prev, isOpen: false }))}
      centered
      size="xl"
      contentClassName={cardClass}
    >
      <Modal.Header closeButton>
        <Modal.Title>Other Amount Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="table-responsive border rounded-1">
          <table className="table text-nowrap mb-0 align-middle table-bordered theme-header-table">
            <thead className="text-dark fs-4">
              <tr>
                {[
                  "No.",
                  "Charge Type",
                  "Pcs",
                  "Ct",
                  "Wt",
                  "Con%",
                  "Rate On",
                  "Rate",
                  "Amount",
                ].map((header, index) => (
                  <th
                    key={header}
                    className="px-2 py-2 text-center"
                    style={{
                      fontSize: "0.85rem",
                      width:
                        otherAmountColumnWidths[
                          header.toLowerCase().replace(/[^a-z]/g, "")
                        ],
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                      position: "relative",
                      userSelect: "none",
                      border: "1px solid #dee2e6",
                    }}
                  >
                    {header}
                    <div
                      className="resize-handle"
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: "5px",
                        cursor: "col-resize",
                        backgroundColor: "transparent",
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const startX = e.clientX;
                        const startWidth = e.currentTarget.parentElement.offsetWidth;
                        const column = header.toLowerCase().replace(/[^a-z]/g, "");

                        const handleMouseMove = (e) => {
                          const diff = e.clientX - startX;
                          const newWidth = Math.max(50, startWidth + diff);
                          handleOtherAmountColumnWidthChange(column, newWidth);
                        };

                        const handleMouseUp = () => {
                          document.removeEventListener("mousemove", handleMouseMove);
                          document.removeEventListener("mouseup", handleMouseUp);
                        };

                        document.addEventListener("mousemove", handleMouseMove);
                        document.addEventListener("mouseup", handleMouseUp);
                      }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {otherAmountModal.entries.map((entry, index) => (
                <tr key={index} style={{ backgroundColor: "#ffffff" }}>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ height: "38px" }}
                    >
                      <span style={{ fontSize: "0.85rem" }}>{index + 1}</span>
                    </div>
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <InlineAutoCompleteInput
                      id={`entry-${index}`}
                      value={entry.chargeType}
                      suggestions={suggestions}
                      chargeOptions={chargeOptions}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        const newEntries = [...otherAmountModal.entries];
                        newEntries[index].chargeType = newValue;
                        setOtherAmountModal((prev) => ({
                          ...prev,
                          entries: newEntries,
                        }));
                        getSuggestions(newValue);
                      }}
                      onComplete={(suggestion) => {
                        const newEntries = [...otherAmountModal.entries];
                        const current = newEntries[index];
                        const next =
                          typeof applyChargeType === "function"
                            ? applyChargeType(current, suggestion)
                            : { ...current, chargeType: suggestion };
                        if (
                          typeof applyChargeType === "function" &&
                          (next.rate !== current.rate ||
                            next.rateOn !== current.rateOn)
                        ) {
                          next.amount = formatAmount(calculateAmount(next));
                        }
                        newEntries[index] = next;
                        setOtherAmountModal((prev) => ({
                          ...prev,
                          entries: newEntries,
                        }));
                        setSuggestions([]);
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                      }}
                      onFocus={(e) => {
                        e.target.style.backgroundColor = "#fff";
                        if (entry.chargeType) {
                          getSuggestions(entry.chargeType);
                        }
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        setTimeout(() => setSuggestions([]), 200);
                      }}
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={entry.pcs}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*$/.test(value)) {
                          const newEntries = [...otherAmountModal.entries];
                          const pcsNum = parseFloat(value);
                          const updatedEntry = {
                            ...newEntries[index],
                            pcs: value,
                          };

                          if (updatedEntry.rate && updatedEntry.rateOn) {
                            updatedEntry.amount = formatAmount(
                              calculateAmount(updatedEntry),
                            );
                          }
                          newEntries[index] = updatedEntry;
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: newEntries,
                          }));
                        }
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) => (e.target.style.backgroundColor = "transparent")}
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={entry.ct}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          const newEntries = [...otherAmountModal.entries];
                          const updatedEntry = {
                            ...newEntries[index],
                            ct: value,
                          };
                          if (updatedEntry.rate && updatedEntry.rateOn) {
                            updatedEntry.amount = formatAmount(
                              calculateAmount(updatedEntry),
                            );
                          }
                          newEntries[index] = updatedEntry;
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: newEntries,
                          }));
                        }
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) => (e.target.style.backgroundColor = "transparent")}
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <div
                      className="d-flex align-items-stretch"
                      style={{ minHeight: "38px" }}
                    >
                      <div
                        className="d-flex flex-column justify-content-center px-1 py-1"
                        style={{ flex: "1 1 0", minWidth: 0 }}
                      >
                        <span
                          className="text-muted"
                          style={{ fontSize: "0.65rem", lineHeight: 1 }}
                        >
                          Total
                        </span>
                        <input
                          type="text"
                          value={entry.wtTotal ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || /^\d*\.?\d*$/.test(value)) {
                              const newEntries = [...otherAmountModal.entries];
                              const updatedEntry = {
                                ...newEntries[index],
                                wtTotal: value,
                              };
                              if (value.trim() !== "") {
                                updatedEntry.wtPP = "";
                                updatedEntry.wt = "";
                              }

                              if (updatedEntry.rate && updatedEntry.rateOn) {
                                updatedEntry.amount = formatAmount(
                                  calculateAmount(updatedEntry),
                                );
                              }

                              newEntries[index] = updatedEntry;
                              setOtherAmountModal((prev) => ({
                                ...prev,
                                entries: newEntries,
                              }));
                            }
                          }}
                          className="form-control border-0 rounded-0"
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.15rem 0.35rem",
                            backgroundColor: "transparent",
                            width: "100%",
                            transition: "background-color 0.2s",
                            textAlign: "left",
                          }}
                          onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                          onBlur={(e) =>
                            (e.target.style.backgroundColor = "transparent")
                          }
                        />
                      </div>
                      <div
                        style={{
                          width: 1,
                          background: "#dee2e6",
                          flexShrink: 0,
                        }}
                        aria-hidden
                      />
                      <div
                        className="d-flex flex-column justify-content-center px-1 py-1"
                        style={{ flex: "1 1 0", minWidth: 0 }}
                      >
                        <span
                          className="text-muted"
                          style={{ fontSize: "0.65rem", lineHeight: 1 }}
                        >
                          PP
                        </span>
                        <input
                          type="text"
                          value={entry.wtPP ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || /^\d*\.?\d*$/.test(value)) {
                              const newEntries = [...otherAmountModal.entries];
                              const updatedEntry = {
                                ...newEntries[index],
                                wtPP: value,
                                wt: value,
                              };
                              if (value.trim() !== "") {
                                updatedEntry.wtTotal = "";
                              }

                              if (updatedEntry.rate && updatedEntry.rateOn) {
                                updatedEntry.amount = formatAmount(
                                  calculateAmount(updatedEntry),
                                );
                              }

                              newEntries[index] = updatedEntry;
                              setOtherAmountModal((prev) => ({
                                ...prev,
                                entries: newEntries,
                              }));
                            }
                          }}
                          className="form-control border-0 rounded-0"
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.15rem 0.35rem",
                            backgroundColor: "transparent",
                            width: "100%",
                            transition: "background-color 0.2s",
                            textAlign: "left",
                          }}
                          onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                          onBlur={(e) =>
                            (e.target.style.backgroundColor = "transparent")
                          }
                        />
                      </div>
                    </div>
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={entry.conPercentage}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          const newEntries = [...otherAmountModal.entries];
                          newEntries[index] = {
                            ...newEntries[index],
                            conPercentage: value,
                          };
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: newEntries,
                          }));
                        }
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) => (e.target.style.backgroundColor = "transparent")}
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <div className="position-relative">
                      <input
                        type="text"
                        value={entry.rateOn}
                        onChange={(e) => {
                          const newEntries = [...otherAmountModal.entries];
                          newEntries[index].rateOn = e.target.value;
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: newEntries,
                          }));
                          setHighlightedIndex(0);
                          const dropdown = e.target.nextElementSibling;
                          if (dropdown) {
                            dropdown.style.display = "block";
                          }
                        }}
                        onKeyDown={(e) => handleRateOnKeyDown(e, index, false)}
                        className="form-control border-0 rounded-0"
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                        }}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#fff";
                          const rect = e.target.getBoundingClientRect();
                          const dropdown = e.target.nextElementSibling;
                          if (dropdown) {
                            const maxWidth = Math.max(
                              ...rateOnOptions.map((item) => {
                                const tempSpan = document.createElement("span");
                                tempSpan.style.fontSize = "0.85rem";
                                tempSpan.style.visibility = "hidden";
                                tempSpan.style.position = "absolute";
                                tempSpan.style.whiteSpace = "nowrap";
                                tempSpan.textContent = item;
                                document.body.appendChild(tempSpan);
                                const width = tempSpan.offsetWidth;
                                document.body.removeChild(tempSpan);
                                return width;
                              }),
                              rect.width,
                            );

                            dropdown.style.display = "block";
                            dropdown.style.position = "fixed";
                            dropdown.style.top = `${rect.bottom}px`;
                            dropdown.style.left = `${rect.left}px`;
                            dropdown.style.width = "auto";
                            dropdown.style.minWidth = `${rect.width}px`;
                            dropdown.style.maxWidth = `${maxWidth + 32}px`;
                            dropdown.style.zIndex = "9999";
                            setHighlightedIndex(0);
                          }
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "transparent";
                          setTimeout(() => {
                            const dropdown = e.target.nextElementSibling;
                            if (dropdown) {
                              dropdown.style.display = "none";
                            }
                          }, 200);
                        }}
                      />
                      <div
                        className="dropdown-menu shadow-sm"
                        style={{
                          display: "none",
                          maxHeight: "200px",
                          overflowY: "auto",
                          backgroundColor: "#fff",
                          position: "fixed",
                          zIndex: 9999,
                          border: "1px solid rgba(0,0,0,.15)",
                          borderRadius: "0.25rem",
                          padding: "0.5rem 0",
                          width: "auto",
                          minWidth: "100%",
                        }}
                      >
                        {rateOnOptions
                          .filter((option) =>
                            option
                              .toLowerCase()
                              .includes((entry.rateOn || "").toLowerCase()),
                          )
                          .map((option, i) => (
                            <div
                              key={i}
                              className={`dropdown-item ${
                                i === highlightedIndex ? "active" : ""
                              }`}
                              style={{
                                cursor: "pointer",
                                padding: "0.25rem 1rem",
                                fontSize: "0.85rem",
                                backgroundColor:
                                  i === highlightedIndex
                                    ? "var(--theme-primary)"
                                    : option.toLowerCase() ===
                                        entry.rateOn.toLowerCase()
                                      ? "#f0f0f0"
                                      : "transparent",
                                color: i === highlightedIndex ? "#fff" : "inherit",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              onMouseEnter={() => setHighlightedIndex(i)}
                              onMouseDown={() => {
                                handleRateOnDropdown(index, option, false);
                                const dropdown = document.querySelector(
                                  '.dropdown-menu[style*="display: block"]',
                                );
                                if (dropdown) {
                                  dropdown.style.display = "none";
                                }
                              }}
                            >
                              {option}
                            </div>
                          ))}
                      </div>
                    </div>
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={entry.rate}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          const newEntries = [...otherAmountModal.entries];
                          const updatedEntry = {
                            ...newEntries[index],
                            rate: value,
                          };
                          if (value && updatedEntry.rateOn) {
                            updatedEntry.amount = formatAmount(
                              calculateAmount(updatedEntry),
                            );
                          } else {
                            updatedEntry.amount = "";
                          }
                          newEntries[index] = updatedEntry;
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: newEntries,
                          }));
                        }
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) => (e.target.style.backgroundColor = "transparent")}
                    />
                  </td>
                  <td
                    className="p-0 position-relative"
                    style={{ border: "1px solid #dee2e6" }}
                  >
                    <input
                      type="text"
                      value={entry.amount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          const newEntries = [...otherAmountModal.entries];
                          newEntries[index].amount = value;
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: newEntries,
                          }));
                        }
                      }}
                      onFocus={(e) => {
                        e.target.style.backgroundColor = "#fff";
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                    />
                  </td>
                </tr>
              ))}
              <tr style={{ backgroundColor: "#ffffff" }}>
                <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ height: "38px" }}
                  >
                    <span style={{ fontSize: "0.85rem" }}>
                      {otherAmountModal.entries.length + 1}
                    </span>
                  </div>
                </td>
                <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                  <InlineAutoCompleteInput
                    id="current-entry"
                    value={otherAmountModal.currentEntry.chargeType}
                    suggestions={suggestions}
                    chargeOptions={chargeOptions}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setOtherAmountModal((prev) => ({
                        ...prev,
                        currentEntry: {
                          ...prev.currentEntry,
                          chargeType: newValue,
                        },
                      }));
                      getSuggestions(newValue);
                    }}
                    onComplete={(suggestion) => {
                      setOtherAmountModal((prev) => {
                        const current = prev.currentEntry;
                        const next =
                          typeof applyChargeType === "function"
                            ? applyChargeType(current, suggestion)
                            : { ...current, chargeType: suggestion };
                        if (
                          typeof applyChargeType === "function" &&
                          (next.rate !== current.rate ||
                            next.rateOn !== current.rateOn)
                        ) {
                          next.amount = formatAmount(calculateAmount(next));
                        }
                        return {
                          ...prev,
                          currentEntry: next,
                        };
                      });
                      setSuggestions([]);
                    }}
                    className="form-control border-0 rounded-0"
                    style={{
                      fontSize: "0.85rem",
                      padding: "0.4rem 0.5rem",
                      backgroundColor: "transparent",
                      width: "100%",
                      transition: "background-color 0.2s",
                    }}
                    onFocus={(e) => {
                      e.target.style.backgroundColor = "#fff";
                      if (otherAmountModal.currentEntry.chargeType) {
                        getSuggestions(otherAmountModal.currentEntry.chargeType);
                      }
                    }}
                    onBlur={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      setTimeout(() => setSuggestions([]), 200);
                    }}
                  />
                </td>
                <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                  <input
                    type="text"
                    value={otherAmountModal.currentEntry.pcs}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*$/.test(value)) {
                        setOtherAmountModal((prev) => {
                          const updatedEntry = {
                            ...prev.currentEntry,
                            pcs: value,
                          };
                          if (updatedEntry.rate && updatedEntry.rateOn) {
                            updatedEntry.amount = formatAmount(
                              calculateAmount(updatedEntry),
                            );
                          }
                          return {
                            ...prev,
                            currentEntry: updatedEntry,
                          };
                        });
                      }
                    }}
                    className="form-control border-0 rounded-0"
                    style={{
                      fontSize: "0.85rem",
                      padding: "0.4rem 0.5rem",
                      backgroundColor: "transparent",
                      width: "100%",
                      transition: "background-color 0.2s",
                      textAlign: "left",
                    }}
                    onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                    onBlur={(e) => (e.target.style.backgroundColor = "transparent")}
                  />
                </td>
                <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                  <input
                    type="text"
                    value={otherAmountModal.currentEntry.ct}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setOtherAmountModal((prev) => {
                          const updatedEntry = {
                            ...prev.currentEntry,
                            ct: value,
                          };
                          if (updatedEntry.rate && updatedEntry.rateOn) {
                            updatedEntry.amount = formatAmount(
                              calculateAmount(updatedEntry),
                            );
                          }
                          return {
                            ...prev,
                            currentEntry: updatedEntry,
                          };
                        });
                      }
                    }}
                    className="form-control border-0 rounded-0"
                    style={{
                      fontSize: "0.85rem",
                      padding: "0.4rem 0.5rem",
                      backgroundColor: "transparent",
                      width: "100%",
                      transition: "background-color 0.2s",
                      textAlign: "left",
                    }}
                    onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                    onBlur={(e) => (e.target.style.backgroundColor = "transparent")}
                  />
                </td>
                <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                  <div
                    className="d-flex align-items-stretch"
                    style={{ minHeight: "38px" }}
                  >
                    <div
                      className="d-flex flex-column justify-content-center px-1 py-1"
                      style={{ flex: "1 1 0", minWidth: 0 }}
                    >
                      <span
                        className="text-muted"
                        style={{ fontSize: "0.65rem", lineHeight: 1 }}
                      >
                        Total
                      </span>
                      <input
                        type="text"
                        value={otherAmountModal.currentEntry.wtTotal ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            setOtherAmountModal((prev) => {
                              const updatedEntry = {
                                ...prev.currentEntry,
                                wtTotal: value,
                              };
                              if (value.trim() !== "") {
                                updatedEntry.wtPP = "";
                                updatedEntry.wt = "";
                              }

                              if (updatedEntry.rate && updatedEntry.rateOn) {
                                updatedEntry.amount = formatAmount(
                                  calculateAmount(updatedEntry),
                                );
                              }

                              return {
                                ...prev,
                                currentEntry: updatedEntry,
                              };
                            });
                          }
                        }}
                        className="form-control border-0 rounded-0"
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.15rem 0.35rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                        onBlur={(e) => (e.target.style.backgroundColor = "transparent")}
                      />
                    </div>
                    <div
                      style={{
                        width: 1,
                        background: "#dee2e6",
                        flexShrink: 0,
                      }}
                      aria-hidden
                    />
                    <div
                      className="d-flex flex-column justify-content-center px-1 py-1"
                      style={{ flex: "1 1 0", minWidth: 0 }}
                    >
                      <span
                        className="text-muted"
                        style={{ fontSize: "0.65rem", lineHeight: 1 }}
                      >
                        PP
                      </span>
                      <input
                        type="text"
                        value={otherAmountModal.currentEntry.wtPP ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            setOtherAmountModal((prev) => {
                              const updatedEntry = {
                                ...prev.currentEntry,
                                wtPP: value,
                                wt: value,
                              };
                              if (value.trim() !== "") {
                                updatedEntry.wtTotal = "";
                              }

                              if (updatedEntry.rate && updatedEntry.rateOn) {
                                updatedEntry.amount = formatAmount(
                                  calculateAmount(updatedEntry),
                                );
                              }

                              return {
                                ...prev,
                                currentEntry: updatedEntry,
                              };
                            });
                          }
                        }}
                        className="form-control border-0 rounded-0"
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.15rem 0.35rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                        onBlur={(e) => (e.target.style.backgroundColor = "transparent")}
                      />
                    </div>
                  </div>
                </td>
                <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                  <input
                    type="text"
                    value={otherAmountModal.currentEntry.conPercentage}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setOtherAmountModal((prev) => ({
                          ...prev,
                          currentEntry: {
                            ...prev.currentEntry,
                            conPercentage: value,
                          },
                        }));
                      }
                    }}
                    className="form-control border-0 rounded-0"
                    style={{
                      fontSize: "0.85rem",
                      padding: "0.4rem 0.5rem",
                      backgroundColor: "transparent",
                      width: "100%",
                      transition: "background-color 0.2s",
                      textAlign: "left",
                    }}
                    onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                    onBlur={(e) => (e.target.style.backgroundColor = "transparent")}
                  />
                </td>
                <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                  <div className="position-relative">
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.rateOn}
                      onChange={(e) => {
                        handleRateOnDropdown(null, e.target.value, true);
                        const dropdown = e.target.nextElementSibling;
                        if (dropdown) {
                          dropdown.style.display = "block";
                        }
                      }}
                      onKeyDown={(e) => handleRateOnKeyDown(e, null, true)}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                      }}
                      onFocus={(e) => {
                        e.target.style.backgroundColor = "#fff";
                        const rect = e.target.getBoundingClientRect();
                        const dropdown = e.target.nextElementSibling;
                        if (dropdown) {
                          const maxWidth = Math.max(
                            ...rateOnOptions.map((item) => {
                              const tempSpan = document.createElement("span");
                              tempSpan.style.fontSize = "0.85rem";
                              tempSpan.style.visibility = "hidden";
                              tempSpan.style.position = "absolute";
                              tempSpan.style.whiteSpace = "nowrap";
                              tempSpan.textContent = item;
                              document.body.appendChild(tempSpan);
                              const width = tempSpan.offsetWidth;
                              document.body.removeChild(tempSpan);
                              return width;
                            }),
                            rect.width,
                          );

                          dropdown.style.display = "block";
                          dropdown.style.position = "fixed";
                          dropdown.style.top = `${rect.bottom}px`;
                          dropdown.style.left = `${rect.left}px`;
                          dropdown.style.width = "auto";
                          dropdown.style.minWidth = `${rect.width}px`;
                          dropdown.style.maxWidth = `${maxWidth + 32}px`;
                          dropdown.style.zIndex = "9999";
                          setHighlightedIndex(0);
                        }
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        setTimeout(() => {
                          const dropdown = e.target.nextElementSibling;
                          if (dropdown) {
                            dropdown.style.display = "none";
                          }
                        }, 200);
                      }}
                    />
                    <div
                      className="dropdown-menu shadow-sm"
                      style={{
                        display: "none",
                        maxHeight: "200px",
                        overflowY: "auto",
                        backgroundColor: "#fff",
                        position: "fixed",
                        zIndex: 9999,
                        border: "1px solid rgba(0,0,0,.15)",
                        borderRadius: "0.25rem",
                        padding: "0.5rem 0",
                        width: "auto",
                        minWidth: "100%",
                      }}
                    >
                      {rateOnOptions
                        .filter((option) =>
                          option
                            .toLowerCase()
                            .includes(
                              (otherAmountModal.currentEntry.rateOn || "").toLowerCase(),
                            ),
                        )
                        .map((option, i) => (
                          <div
                            key={i}
                            className={`dropdown-item ${
                              i === highlightedIndex ? "active" : ""
                            }`}
                            style={{
                              cursor: "pointer",
                              padding: "0.25rem 1rem",
                              fontSize: "0.85rem",
                              backgroundColor:
                                i === highlightedIndex
                                  ? "var(--theme-primary)"
                                  : option.toLowerCase() ===
                                      otherAmountModal.currentEntry.rateOn.toLowerCase()
                                    ? "#f0f0f0"
                                    : "transparent",
                              color: i === highlightedIndex ? "#fff" : "inherit",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            onMouseEnter={() => setHighlightedIndex(i)}
                            onMouseDown={() => {
                              handleRateOnDropdown(null, option, true);
                              const dropdown = document.querySelector(
                                '.dropdown-menu[style*="display: block"]',
                              );
                              if (dropdown) {
                                dropdown.style.display = "none";
                              }
                            }}
                          >
                            {option}
                          </div>
                        ))}
                    </div>
                  </div>
                </td>
                <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                  <input
                    type="text"
                    value={otherAmountModal.currentEntry.rate}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setOtherAmountModal((prev) => {
                          const updatedEntry = {
                            ...prev.currentEntry,
                            rate: value,
                          };
                          if (value && updatedEntry.rateOn) {
                            updatedEntry.amount = formatAmount(
                              calculateAmount(updatedEntry),
                            );
                          } else {
                            updatedEntry.amount = "";
                          }
                          return {
                            ...prev,
                            currentEntry: updatedEntry,
                          };
                        });
                      }
                    }}
                    className="form-control border-0 rounded-0"
                    style={{
                      fontSize: "0.85rem",
                      padding: "0.4rem 0.5rem",
                      backgroundColor: "transparent",
                      width: "100%",
                      transition: "background-color 0.2s",
                      textAlign: "left",
                    }}
                    onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                    onBlur={(e) => (e.target.style.backgroundColor = "transparent")}
                  />
                </td>
                <td
                  className="p-0 position-relative"
                  style={{ border: "1px solid #dee2e6" }}
                >
                  <input
                    type="text"
                    value={otherAmountModal.currentEntry.amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setOtherAmountModal((prev) => ({
                          ...prev,
                          currentEntry: {
                            ...prev.currentEntry,
                            amount: value,
                          },
                        }));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const value = otherAmountModal.currentEntry.amount;
                        if (value && !isNaN(value) && Number(value) > 0) {
                          e.preventDefault();
                          const finalEntries = [
                            ...otherAmountModal.entries,
                            otherAmountModal.currentEntry,
                          ];

                          const totalAmount = finalEntries.reduce(
                            (sum, entry) => sum + Number(entry.amount || 0),
                            0,
                          );

                          const totalLessWeight = finalEntries.reduce((sum, entry) => {
                            const lessWeight = calculateLessWeight(entry);
                            return sum + (isNaN(lessWeight) ? 0 : lessWeight);
                          }, 0);

                          setTableData((prev) => {
                            const newData = [...prev];
                            const row = newData[otherAmountModal.rowIndex];

                            if (!row || !row.data) return prev;

                            const othAmtCell = row.data.find(
                              (cell) => cell.id === "othAmt",
                            );
                            if (othAmtCell) {
                              othAmtCell.value = formatAmount(totalAmount);
                              othAmtCell.otherAmountEntries = finalEntries;
                            }

                            return newData;
                          });

                          setOtherAmountModal((prev) => ({
                            ...prev,
                            isOpen: false,
                          }));
                        }
                      }
                    }}
                    onFocus={(e) => {
                      e.target.style.backgroundColor = "#fff";
                      setTimeout(() => {
                        setOtherAmountModal((prev) => {
                          if (
                            prev.currentEntry.chargeType ||
                            prev.currentEntry.pcs ||
                            prev.currentEntry.ct ||
                            prev.currentEntry.wt ||
                            prev.currentEntry.wtPP ||
                            prev.currentEntry.wtTotal ||
                            prev.currentEntry.conPercentage ||
                            prev.currentEntry.rateOn ||
                            prev.currentEntry.rate
                          ) {
                            return {
                              ...prev,
                              entries: [...prev.entries],
                              currentEntry: { ...prev.currentEntry },
                            };
                          }
                          return prev;
                        });
                      }, 0);
                    }}
                    onBlur={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      const value = otherAmountModal.currentEntry.amount;
                      if (value && !isNaN(value) && Number(value) > 0) {
                        setOtherAmountModal((prev) => ({
                          ...prev,
                          entries: [...prev.entries, { ...prev.currentEntry }],
                          currentEntry: {
                            chargeType: "",
                            pcs: "",
                            ct: "",
                            wt: "",
                            wtPP: "",
                            wtTotal: "",
                            conPercentage: "",
                            rateOn: "",
                            rate: "",
                            amount: "",
                          },
                        }));
                        setTimeout(() => {
                          const firstInput = document.querySelector(
                            ".modal-body tbody tr:last-child td:nth-child(2) input",
                          );
                          if (firstInput) {
                            firstInput.focus();
                          }
                        }, 0);
                      }
                    }}
                    className="form-control border-0 rounded-0"
                    style={{
                      fontSize: "0.85rem",
                      padding: "0.4rem 0.5rem",
                      backgroundColor: "transparent",
                      width: "100%",
                      transition: "background-color 0.2s",
                      textAlign: "left",
                    }}
                  />
                </td>
              </tr>
            </tbody>
            <tfoot>{/* Remove the total row from table */}</tfoot>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-4">
          <div className="d-flex align-items-center gap-2">
            <span className="text-dark" style={{ fontSize: "0.9rem" }}>
              LWt:
            </span>
            <span className="fw-bold" style={{ fontSize: "0.9rem" }}>
              {formatLessWeight(
                otherAmountModal.entries.reduce((sum, entry) => {
                  const lessWeight = calculateLessWeight(entry);
                  return sum + (isNaN(lessWeight) ? 0 : lessWeight);
                }, 0),
              )}
            </span>
          </div>
          <div
            className="border-end h-100 mx-2"
            style={{ width: "1px", backgroundColor: "#dee2e6" }}
          ></div>
          <div className="d-flex align-items-center gap-2">
            <span className="text-dark" style={{ fontSize: "0.9rem" }}>
              Total:
            </span>
            <span className="fw-bold" style={{ fontSize: "0.9rem" }}>
              ₹ {formatAmount(calculateTotal(otherAmountModal.entries))}
            </span>
          </div>
        </div>
        <Button
          variant="primary"
          className={buttonClass}
          onClick={handleSaveOtherAmountEntries}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>,
    document.getElementById("modal-portal"),
  );
};

export default OtherAmountModal;
